import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/format';
import { peakOf } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { eventsSummary } from './analytics';
import type { EventLike } from './analytics';

export interface EventSummaryProps {
  events: EventLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

/** The Event Management Summary tab: tiles, then report panels and watch-lists. */
const EventSummaryView = ({ events, loading, isDarkMode }: EventSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => eventsSummary(events), [events]);
  const statusPeak = peakOf(s.byStatus, (r) => r.count);
  const categoryPeak = peakOf(s.byCategory, (r) => r.count);
  const monthPeak = peakOf(s.months, (r) => r.count);
  const statusColor = (name: string) => (name === 'Completed' ? t.income : name === 'Cancelled' ? t.danger : t.info);
  const statusTone = (name: string) => (name === 'Completed' ? 'success' : name === 'Cancelled' ? 'error' : 'accent');

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Total Events" value={String(s.total)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Upcoming" value={String(s.upcomingCount)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Next 30 Days" value={String(s.next30.length)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="This Month" value={String(s.thisMonth)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Completed" value={String(s.completedCount)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Cancelled" value={String(s.cancelledCount)} color={s.cancelledCount ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Planned Budget" value={formatCurrency(s.plannedBudget)} color={t.warning} isDarkMode={isDarkMode} />
        <KpiCard label="Spent (Completed)" value={formatCurrency(s.completedSpent)} color={t.expense} isDarkMode={isDarkMode} />
        <KpiCard label="Turnout vs Expected" value={s.turnoutPercent === null ? '—' : `${s.turnoutPercent}%`} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Needs Follow-up" value={String(s.followUps.length)} color={s.followUps.length ? t.danger : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Events by Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={statusPeak} color={statusColor(r.name)} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Events', align: 'right' }
          ]}
          rows={s.byStatus}
          rowKey="name"
          emptyMessage="No events yet."
          below={s.total ? <TotalRow label="Total events" value={s.total} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Events by Category"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Category', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={categoryPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Events', align: 'right' },
            { key: 'total', header: 'Budget', align: 'right', nowrap: true, render: (r) => formatCurrency(r.total) }
          ]}
          rows={s.byCategory}
          rowKey="name"
          emptyMessage="No events yet."
          below={s.byCategory.length ? <TotalRow label="Planned budget (not cancelled)" value={formatCurrency(s.plannedBudget)} color={t.warning} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <TablePanel
        title="Events by Month — Last 12 Months"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="480px"
        columns={[
          { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={monthPeak} color={t.info} isDarkMode={isDarkMode} /> },
          { key: 'count', header: 'Events', align: 'right' },
          { key: 'total', header: 'Budget', align: 'right', nowrap: true, render: (r) => formatCurrency(r.total) }
        ]}
        rows={s.months}
        rowKey="month"
        emptyMessage="No events recorded yet."
        below={<TotalRow label="Events in 12 months (not cancelled)" value={s.months.reduce((n, m) => n + m.count, 0)} isDarkMode={isDarkMode} />}
      />

      <TablePanel
        title="Coming Up — Next 30 Days"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="620px"
        columns={[
          { key: 'eventDate', header: 'When', nowrap: true, render: (r) => <strong>{formatDateTime(r.eventDate, r.time)}</strong> },
          { key: 'eventName', header: 'Event', render: (r) => r.eventName || '-' },
          { key: 'category', header: 'Category', render: (r) => (r.category ? <Badge tone="neutral">{r.category}</Badge> : '-') },
          { key: 'venue', header: 'Venue', wrap: true, render: (r) => r.venue || '-' },
          { key: 'organiser', header: 'Organiser', render: (r) => r.organiser || '-' },
          { key: 'participants', header: 'Expected', align: 'right', render: (r) => (r.participants ? String(r.participants) : '-') }
        ]}
        rows={s.next30}
        rowKey={(r) => `${r.eventId}|${r.eventDate}|${r.time}`}
        emptyMessage="Nothing scheduled in the next 30 days."
      />

      <TablePanel
        title="Completed Events — Budget vs Actual"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="680px"
        columns={[
          { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.event.eventDate) },
          { key: 'name', header: 'Event', render: (r) => <strong>{r.event.eventName || '-'}</strong> },
          { key: 'budget', header: 'Budget', align: 'right', nowrap: true, render: (r) => formatCurrency(r.budget) },
          { key: 'actualCost', header: 'Actual', align: 'right', nowrap: true, render: (r) => (r.variance === null ? '—' : formatCurrency(r.actualCost)) },
          {
            key: 'variance', header: 'Variance', align: 'right', nowrap: true,
            render: (r) => (r.variance === null ? '—' : <span style={{ color: r.variance < 0 ? t.danger : t.income, fontWeight: 'bold' }}>{formatCurrency(r.variance)}</span>)
          },
          {
            key: 'turnout', header: 'Turnout (actual / expected)', nowrap: true,
            render: (r) => (r.expected > 0 && r.event.actualParticipants
              ? <BarCell value={r.actual} max={r.expected} showPercent color={r.actual >= r.expected ? t.income : t.warning} isDarkMode={isDarkMode} />
              : '—')
          }
        ]}
        rows={s.outcomes}
        rowKey={(r) => `${r.event.eventId}|${r.event.eventDate}`}
        emptyMessage="No completed events yet."
        below={s.outcomes.length ? (
          <>
            <TotalRow label="Budgeted" value={formatCurrency(s.completedBudget)} isDarkMode={isDarkMode} />
            <TotalRow label={`Spent${s.overBudget ? ` (${s.overBudget} over budget)` : ''}`} value={formatCurrency(s.completedSpent)} color={s.completedSpent > s.completedBudget ? t.danger : t.income} isDarkMode={isDarkMode} />
            {s.turnoutPercent !== null && (
              <TotalRow label="Turnout" value={`${s.actualTurnout} of ${s.expectedTurnout} expected (${s.turnoutPercent}%)`} isDarkMode={isDarkMode} />
            )}
          </>
        ) : null}
      />

      <TablePanel
        title="Needs Follow-up"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="560px"
        columns={[
          { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.event.eventDate) },
          { key: 'name', header: 'Event', render: (r) => <strong>{r.event.eventName || '-'}</strong> },
          { key: 'status', header: 'Status', render: (r) => { const st = r.event.eventStatus || 'Completed'; return <Badge tone={statusTone(st)}>{st}</Badge>; } },
          { key: 'reason', header: 'Why', wrap: true, render: (r) => <span style={{ color: t.warning }}>{r.reason}</span> }
        ]}
        rows={s.followUps}
        rowKey={(r) => `${r.event.eventId}|${r.event.eventDate}`}
        emptyMessage="Every past event is closed with its actuals."
      />
    </>
  );
};

export default EventSummaryView;
